import "./receptionTypes.js";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { faXmark } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { status } from "shared/src/appointment.mjs";
import * as f from "../../utils/formatter";
import * as api from "../../services/api";
import { useSnackbarMessage } from "../Snackbar.jsx";

/**
 * A component to display a confirmation modal for moving an appointment to a different status.
 *
 * @typedef {object} ConfirmationModalProps
 * @property {Appointment} appointment
 * @property {function} handleCloseConfirmationModal Function to close confirmation modal
 * @property {boolean} openConfirmationModal Flag to open confirmation modal
 * @property {number} newStatus New status Id for the appointment
 *
 * @param {ConfirmationModalProps} props
 * @returns {JSX.Element}
 */

export const ConfirmationModal = ({
  appointments,
  appointment,
  handleCloseConfirmationModal,
  newStatus,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const displaySnackbarMessage = useSnackbarMessage();
  const handleMoveAppointment = async () => {
    handleCloseConfirmationModal();
    onConfirm?.();

    if (newStatus === status.BOOKED) {
      const parent =
        appointment.parent_id == null
          ? null
          : appointments.find((a) => a.id === appointment.parent_id);

      const payload = [
        { id: appointment.id, status_id: newStatus },
        parent == null ? null : { id: parent.id, status_id: newStatus },
      ].filter((d) => d != null);

      await api.patchAppointments(payload);
    } else {
      const payload = appointment.children?.map((a) => ({
        id: a.id,
        status_id: newStatus,
      })) ?? [{ id: appointment.id, status_id: newStatus }];

      await api.patchAppointments(payload);
    }

    displaySnackbarMessage({
      title: t("reception.info"),
      autoHideDisabled: true,
      content: t(
        newStatus === status.CONFIRMED || newStatus === status.WAITING
          ? "reception.client_moved_checked_in"
          : "reception.client_moved_booked",
        {
          name: f.fullName(appointment.client),
        },
      ),
    });
  };

  return (
    <Dialog open onClose={handleCloseConfirmationModal} maxWidth="xs">
      <DialogTitle
        sx={{
          padding: "2.4rem 2rem",
          margin: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {(newStatus === status.CONFIRMED || newStatus === status.WAITING) &&
          t("reception.move_to_checked_in")}
        {newStatus === status.BOOKED && t("reception.move_to_booked")}
        <IconButton aria-label="close" onClick={handleCloseConfirmationModal}>
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "0 4.4rem" }}>
        <strong>{f.fullName(appointment.client)}</strong>{" "}
        {(newStatus === status.CONFIRMED || newStatus === status.WAITING) &&
          t("reception.will_be_checked_in")}
        {newStatus === status.BOOKED && t("reception.will_be_booked")}
      </DialogContent>
      <DialogActions sx={{ padding: "3.5rem 2rem 3rem 2rem" }}>
        <Button variant="outlined" onClick={handleCloseConfirmationModal}>
          {t("reception.cancel")}
        </Button>
        <Button variant="contained" onClick={handleMoveAppointment}>
          {t("reception.yes")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
